import { NavLink, Outlet } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import {
  BellRing, LayoutDashboard, Rss, Bell, ListChecks, Megaphone, Radio,
  AtSign, Clock, BellDot, Settings, Radar,
} from "lucide-react";
import api from "@/lib/api";
import { useHotelContext } from "@/contexts/HotelContext";

const TABS = [
  { to: "/operations-hub",                label: "Dashboard",   icon: LayoutDashboard, end: true, testid: "ops-tab-dashboard" },
  { to: "/operations-hub/feed",           label: "Live Feed",   icon: Rss,             testid: "ops-tab-feed" },
  { to: "/operations-hub/notifications",  label: "Notifications", icon: Bell,          testid: "ops-tab-notifs" },
  { to: "/operations-hub/tasks",          label: "Tasks",       icon: ListChecks,      testid: "ops-tab-tasks" },
  { to: "/operations-hub/ai-decisions",   label: "AI Decisions", icon: Radar,          testid: "ops-tab-ai-decisions", badge: "aiDecisionsCount" },
  { to: "/operations-hub/broadcasts",     label: "Broadcasts",  icon: Radio,           testid: "ops-tab-broadcasts" },
  { to: "/operations-hub/announcements",  label: "Announcements", icon: Megaphone,     testid: "ops-tab-announcements" },
  { to: "/operations-hub/mentions",       label: "Mentions",    icon: AtSign,          testid: "ops-tab-mentions" },
  { to: "/operations-hub/timeline",       label: "Timeline",    icon: Clock,           testid: "ops-tab-timeline" },
  { to: "/operations-hub/subscriptions",  label: "Subscriptions", icon: BellDot,       testid: "ops-tab-subscriptions" },
  { to: "/operations-hub/settings",       label: "Settings",    icon: Settings,        testid: "ops-tab-settings" },
];

export default function OpsShell() {
  const { ctx } = useHotelContext() || {};
  const hotelId = ctx?.hotel_id;
  const [aiDecisionsCount, setAiDecisionsCount] = useState(0);

  const pollBadge = useCallback(async () => {
    if (!hotelId) return;
    try {
      const { data } = await api.get("/ai-ops/decisions", {
        params: { hotel_id: hotelId, status: "pending", limit: 200 },
      });
      setAiDecisionsCount((data.items || []).length);
    } catch { /* silent — badge is best-effort */ }
  }, [hotelId]);

  useEffect(() => {
    pollBadge();
    const iv = setInterval(pollBadge, 30000);
    return () => clearInterval(iv);
  }, [pollBadge]);

  const badges = { aiDecisionsCount };

  return (
    <div className="space-y-6" data-testid="ops-shell">
      <div className="flex items-baseline gap-3">
        <div className="w-11 h-11 rounded-xl bg-hocp-gold flex items-center justify-center shrink-0">
          <BellRing className="w-5 h-5 text-hocp-eerie" strokeWidth={2}/>
        </div>
        <div>
          <h1 className="font-display text-[26px] font-bold text-hocp-navy leading-none">
            Operations Hub
          </h1>
          <p className="text-[13px] text-slate-500 mt-1.5">
            Every operational signal, one screen · Live Feed · Notifications · Tasks · AI Decisions · Broadcasts · Announcements.
          </p>
        </div>
      </div>
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {TABS.map(t => {
          const Icon = t.icon;
          const badgeValue = t.badge ? badges[t.badge] : null;
          return (
            <NavLink key={t.to} to={t.to} end={t.end} data-testid={t.testid}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium border-b-2 whitespace-nowrap ${
                  isActive ? "border-hocp-navy text-hocp-navy" : "border-transparent text-slate-500 hover:text-slate-800"
                }`}>
              <Icon className="w-4 h-4" strokeWidth={1.75}/>
              {t.label}
              {badgeValue > 0 && (
                <span data-testid={`${t.testid}-badge`}
                      className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-rose-500 text-white text-[10.5px] font-semibold">
                  {badgeValue > 99 ? "99+" : badgeValue}
                </span>
              )}
            </NavLink>
          );
        })}
      </div>
      <Outlet/>
    </div>
  );
}
